import { Button, Collapse, Tag, Flex } from 'antd'
import moment from 'moment-timezone'
import { EyeOutlined } from '@ant-design/icons'
import { SpecTest } from '@signifyd/cypress'
import { Text } from '@signifyd/components'
import TraceDownloader from 'pages/TraceViewer/TraceDownloader'
import { statusTag } from 'utils/utils'
import { ColumnType } from 'antd/lib/table'
import { getTraceFileForTestAttachments } from 'lib/queries/useGetSignedTrace/trace.utils'
import { RunHandle } from 'lib/queries/types'
import styles from './SpecFileDetails.module.less'

export const selectedRunColumns = (
  runHandle: RunHandle
): Array<ColumnType<SpecTest>> => {
  return [
    {
      title: 'Test Case',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => <Text weight="semibold">{title}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Passed', value: 'passed' },
        { text: 'Failed', value: 'failed' },
        { text: 'Skipped', value: 'skipped' },
        { text: 'Flaky', value: 'flaky' },
        { text: 'Timed Out', value: 'timedOut' },
        { text: 'Interrupted', value: 'interrupted' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: 'passed' | 'failed' | 'skipped') => (
        <Tag icon={statusTag[status]?.icon} color={statusTag[status]?.color}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Attachments',
      dataIndex: 'attachments',
      key: 'attachments',
      render: (attachments: Array<string> | undefined) => {
        const traceFile = getTraceFileForTestAttachments(attachments)

        if (!traceFile) {
          return <Text type="secondary">No Attachments</Text>
        }

        const traceRequest = {
          ...runHandle,
          traceFile,
        }

        return (
          <Flex gap="10px">
            <Button
              className={styles.buttonContainer}
              icon={<EyeOutlined />}
              type="link"
              onClick={() =>
                window.open(
                  `/playwright/trace-viewer?project=${traceRequest.project}&runId=${traceRequest.runId}&traceFile=${traceFile}&suite=${traceRequest.suite}`
                )
              }
            >
              View Trace
            </Button>
            <TraceDownloader traceRequest={traceRequest} />
          </Flex>
        )
      },
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      sorter: (a, b) => {
        if (!a || !b) {
          return 0
        }

        return a.duration - b.duration
      },
      render: (duration: number) =>
        `${moment
          .duration(duration, 'milliseconds')
          .asSeconds()
          .toFixed(2)} seconds`,
    },
    {
      title: 'Failure Details',
      dataIndex: 'failureDetails',
      key: 'failureDetails',
      render: (failureDetails?: string) => {
        if (!failureDetails) {
          return null
        }

        return (
          <Collapse
            ghost
            items={[
              {
                label: (
                  <Button className={styles.customLinkButton} type="link">
                    View Failure Details
                  </Button>
                ),
                children: (
                  <Text paragraph>
                    <pre className={styles.preStyle}>{failureDetails}</pre>
                  </Text>
                ),
                styles: {
                  header: { padding: 0 },
                  body: { padding: 0 },
                },
              },
            ]}
          />
        )
      },
    },
    {
      title: 'Console/Debug Output',
      dataIndex: 'consoleOutput',
      key: 'consoleOutput',
      render: (consoleOutput?: string) => {
        if (!consoleOutput) {
          return <Text type="secondary">No Output</Text>
        }

        return (
          <Collapse
            ghost
            items={[
              {
                label: (
                  <Button className={styles.collapseHeaderButton} type="link">
                    View Console Output
                  </Button>
                ),
                children: (
                  <Text paragraph>
                    <pre className={styles.preStyle}>{consoleOutput}</pre>
                  </Text>
                ),
                styles: {
                  header: { padding: 0 },
                  body: { padding: 0 },
                },
              },
            ]}
          />
        )
      },
    },
    {
      title: 'Test Errors',
      dataIndex: 'errorStack',
      key: 'errorStack',
      render: (errorStack: string | undefined) => {
        if (!errorStack) {
          return <Text type="secondary">No Errors</Text>
        }

        return (
          <Collapse
            ghost
            items={[
              {
                label: (
                  <Button className={styles.collapseHeaderButton} type="link">
                    View Test Errors
                  </Button>
                ),
                children: (
                  <Text paragraph>
                    <pre className={styles.preStyle}>{errorStack}</pre>
                  </Text>
                ),
                styles: {
                  header: { padding: 0 },
                  body: { padding: 0 },
                },
              },
            ]}
          />
        )
      },
    },
  ]
}
